/**
 * OpenSolar Project Status Badge Component
 *
 * This component displays a badge with color indicating the status of an OpenSolar project.
 */
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

type StatusBadgeProps = {
  status: number;
};

/**
 * Mapping OpenSolar project status codes to readable names and styles
 *
 * The OpenSolar API represents project statuses as numeric codes:
 * - 0: Lead
 * - 10: Design
 * - 20: Proposal
 * - 30: Contract
 * - 40: Install
 * - 50: Complete
 * - 60: Cancelled
 */
export function StatusBadge({ status }: StatusBadgeProps) {
  // Convert numeric status to display text and variant
  const getStatusInfo = (status: number) => {
    switch (status) {
      case 0:
        return { text: 'Lead', variant: 'outline' as const };
      case 10:
        return { text: 'Design', variant: 'secondary' as const };
      case 20:
        return { text: 'Proposal', variant: 'secondary' as const };
      case 30:
        return { text: 'Contract', variant: 'default' as const };
      case 40:
        return { text: 'Install', variant: 'default' as const };
      case 50:
        return { text: 'Complete', variant: 'success' as const };
      case 60:
        return { text: 'Cancelled', variant: 'destructive' as const };
      default:
        return { text: 'Unknown', variant: 'outline' as const };
    }
  };

  const { text, variant } = getStatusInfo(status);

  // Custom className for status-specific colors
  const getCustomClass = () => {
    switch (status) {
      case 0: // Lead
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
      case 10: // Design
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 20: // Proposal
        return 'bg-purple-100 text-purple-800 hover:bg-purple-200';
      case 30: // Contract
        return 'bg-amber-100 text-amber-800 hover:bg-amber-200';
      case 40: // Install
        return 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200';
      case 50: // Complete
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 60: // Cancelled
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  return (
    <Badge
      variant={variant}
      className={cn('font-medium', variant === 'outline' && getCustomClass())}
    >
      {text}
    </Badge>
  );
}
